import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { TaskPeopleTime, WorkUsage } from '@shared/types/factory';
import { LoadError } from '@/components/LoadError';
import { LoadingState } from '@/components/LoadingState';
import { StatTile } from '@/components/StatTile';
import { MemberAvatar } from '@/components/team/MemberAvatar';
import { EMPTY } from '@/lib/empty';
import { formatNumber, formatRelative } from '@/lib/formatters';
import { cachedShare, formatAgentTime, formatTokens, settingLabel } from '@/lib/usage-format';
import { runPath } from '@/navigation/project-paths';
import { runRef } from './run-status';

export interface TaskUsageCardProps {
  /** GET /api/usage?task_id='s agent usage; undefined while it loads. */
  usage: WorkUsage | undefined;
  /** The same answer's `people`. */
  people: TaskPeopleTime | undefined;
  error?: string | null;
  onRetry?: () => void;
  /** The runs link to their pages. */
  projectSlug?: string;
  onNavigate?: () => void;
}

const MIN = 60_000;
const plural = (n: number, word: string) => `${formatNumber(n)} ${word}${n === 1 ? '' : 's'}`;

/**
 * What a task has cost (T-12, T-102; one card since SF-T-305): a row of figures (agent time, output and input
 * tokens, the last run, people's time), then aligned rows under a muted label: each model setting, each
 * factory run (linking to it) and each person, the figures right-aligned. Machines report T3 runs a few
 * minutes after they finish, so a running one isn't in it yet. A person's minute counts when their tab was
 * visible, focused and in use.
 */
export function TaskUsageCard({ usage, people, error, onRetry, projectSlug, onNavigate }: TaskUsageCardProps) {
  if ((!usage || !people) && error) return <LoadError compact title="Couldn't load the usage" error={error} onRetry={onRetry} />;
  if (!usage || !people) return <LoadingState inline label="Loading usage…" />;
  const t = usage.totals;
  if (!t.runs && !people.minutes) {
    return <p className="text-xs text-muted-foreground">Nothing recorded yet. Machines report each T3 run a few minutes after it finishes.</p>;
  }
  const cached = cachedShare(t);
  const count = people.by_person.length;
  return (
    <div className="space-y-2 text-xs">
      {error ? <LoadError compact title="Couldn't refresh the usage" error={error} onRetry={onRetry} /> : null}
      {/* Capped, so on a wide pane the figures stay near what they belong to. */}
      <div className="max-w-3xl divide-y rounded-md border">
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 px-3 py-2 sm:grid-cols-5">
          <StatTile frame="none" size="sm" label="Agent time" value={t.runs ? formatAgentTime(t.ms) : EMPTY} sub={t.runs ? `${plural(t.runs, 'T3 run')} · ${plural(t.threads, 'thread')}` : 'None yet'} />
          <StatTile frame="none" size="sm" label="Output tokens" value={t.runs ? formatTokens(t.output_tokens) : EMPTY} sub={t.reasoning_tokens ? `${formatTokens(t.reasoning_tokens)} reasoning` : null} />
          <StatTile frame="none" size="sm"
            label="Input tokens"
            value={t.runs ? formatTokens(t.input_tokens) : EMPTY}
            sub={cached !== null ? `${formatNumber(cached, { maximumFractionDigits: 0 })}% from cache` : null}
          />
          <StatTile frame="none" size="sm" label="Last run" value={usage.last_at ? formatRelative(usage.last_at) : EMPTY} />
          <StatTile frame="none" size="sm"
            label="People's time"
            value={people.minutes ? formatAgentTime(people.minutes * MIN) : EMPTY}
            sub={people.minutes ? `${formatNumber(count)} ${count === 1 ? 'person' : 'people'}` : 'None yet'}
          />
        </div>
        {usage.by_setting.length ? (
          <Rows label="Model" aria-label="By model setting">
            {usage.by_setting.map((s) => (
              <Row
                key={`${s.provider}:${s.model}:${s.effort}:${s.speed}`}
                name={<span className="font-medium">{settingLabel(s)}</span>}
                figures={`${formatAgentTime(s.ms)} · ${plural(s.runs, 'run')}${s.output_tokens !== null ? ` · ${formatTokens(s.output_tokens)} out` : ''}`}
              />
            ))}
          </Rows>
        ) : null}
        {usage.by_run.length ? (
          <Rows label="Runs" aria-label="By run">
            {usage.by_run.map((r) => (
              <Row
                key={r.run?.id ?? 'outside'}
                name={
                  r.run && projectSlug ? (
                    <Link to={runPath(projectSlug, r.run.number)} onClick={onNavigate} className="font-medium hover:underline">
                      {runRef(r.run.number)}
                    </Link>
                  ) : (
                    <span className="font-medium">{r.run ? runRef(r.run.number) : 'Outside factory runs'}</span>
                  )
                }
                figures={`${formatAgentTime(r.ms)} · ${plural(r.runs, 'T3 run')}${r.output_tokens !== null ? ` · ${formatTokens(r.output_tokens)} out` : ''}`}
              />
            ))}
          </Rows>
        ) : null}
        {people.by_person.length ? (
          <Rows label="People" aria-label="By person">
            {people.by_person.map((p) => (
              <Row
                key={p.person.id}
                name={
                  <span className="flex min-w-0 items-center gap-1.5">
                    <MemberAvatar name={p.person.name} photourl={p.person.photo_url} size="xs" hint={false} />
                    <span className="truncate font-medium">{p.person.name}</span>
                  </span>
                }
                figures={`${formatAgentTime(p.minutes * MIN)} · ${formatRelative(p.last_at)}`}
              />
            ))}
          </Rows>
        ) : null}
      </div>
    </div>
  );
}

/** A group of rows under a muted label in a narrow first column. */
function Rows({ label, children, 'aria-label': ariaLabel }: { label: string; children: ReactNode; 'aria-label': string }) {
  return (
    <div className="flex gap-3 px-3 py-1.5">
      <span className="w-14 shrink-0 pt-px text-2xs text-muted-foreground">{label}</span>
      <ul className="min-w-0 flex-1 space-y-0.5" aria-label={ariaLabel}>
        {children}
      </ul>
    </div>
  );
}

function Row({ name, figures }: { name: ReactNode; figures: string }) {
  return (
    <li className="flex min-h-5 items-center gap-3">
      <span className="min-w-0 truncate">{name}</span>
      <span className="ml-auto shrink-0 text-muted-foreground tabular-nums">{figures}</span>
    </li>
  );
}
